import * as React from 'react';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import EmissionChart from './EmissionChart';
import Histogram from './Histogram';
import HistogramSummaryGrid from './HistogramSummaryGrid';
import EmissionDataGrid from './EmissionDataGrid';
import CustomDatePicker from './CustomDatePicker';
import dayjs, { Dayjs } from 'dayjs';
import SelectAggregation from './SelectAggregation';
import { DataItem } from "../internals/interfaces";
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ExpandableDataGrid from './ExpandableDataGrid';
import TableChartIcon from '@mui/icons-material/TableChart';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { processPercentileData, PercentileData } from './utils/percentileDataProcessor';
import { useMediaQuery } from '@mui/material';
import ApiClient from '../../utils/apiClient';
import {
  getDailyCacheKey,
  getCachedData,
  setDailyCachedData,
  createCacheKeyFromParams,
  getDashboardPreferences,
  setDashboardPreferences
} from '../../utils/cacheUtils';
import { isFreeUser } from '../../utils/subscriptionAccess';

export default function Emissions() {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [electricityLoading, setElectricityLoading] = React.useState(true);
  const [gasLoading, setGasLoading] = React.useState(true);
  const [electricityData, setElectricityData] = React.useState<DataItem[]>([]);
  const [gasData, setGasData] = React.useState<DataItem[]>([]);
  const [percentileData, setPercentileData] = React.useState<PercentileData | null>(null);
  const [percentileLoading, setPercentileLoading] = React.useState(true);
  const [gasPercentileData, setGasPercentileData] = React.useState<PercentileData | null>(null);
  const [gasPercentileLoading, setGasPercentileLoading] = React.useState(true);
  
  // Load cached preferences on mount
  const cachedPrefs = React.useMemo(() => getDashboardPreferences('emissions'), []);
  const [dateFrom, setDateFrom] = React.useState<Dayjs>(
    cachedPrefs?.dateFrom ? dayjs(cachedPrefs.dateFrom) : dayjs('2024-09-13')
  );
  const [dateTo, setDateTo] = React.useState<Dayjs>(
    cachedPrefs?.dateTo ? dayjs(cachedPrefs.dateTo) : dayjs('2024-09-13')
  );
  const [aggregation, setAggregation] = React.useState<string>(
    cachedPrefs?.aggregation || 'day'
  );
  
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mostRecentCompleteDay, setMostRecentCompleteDay] = React.useState<Dayjs | null>(null);
  const [controlsExpanded, setControlsExpanded] = React.useState(false);
    const [refreshTrigger, setRefreshTrigger] = React.useState(0);
  const [refreshData, setRefreshData] = React.useState(false);
  const [electricityDays, setElectricityDays] = React.useState<string>('all');
  const [gasDays, setGasDays] = React.useState<string>('all');
  const open = Boolean(anchorEl);

  // Save preferences to cache whenever they change
  React.useEffect(() => {
    if (isInitialized) {
      setDashboardPreferences('emissions', {
        dateFrom: dateFrom.format('YYYY-MM-DD'),
        dateTo: dateTo.format('YYYY-MM-DD'),
        aggregation: aggregation
      });
    }
  }, [dateFrom, dateTo, aggregation, isInitialized]);

  // Listen for dashboard refresh events
  React.useEffect(() => {
    const handleDashboardRefresh = () => {      
      setElectricityLoading(true);
      setGasLoading(true);
      setPercentileLoading(true);
      setGasPercentileLoading(true);
      setRefreshData(false);
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('dashboardRefresh', handleDashboardRefresh);
    return () => {
      window.removeEventListener('dashboardRefresh', handleDashboardRefresh);
    };
  }, []);

  const maxDaysNoAggregation = 0
  const maxDaysDayAggregation = 30

  const handleDateChangeFrom = (x: Dayjs | null) => { 
    const date = x ?? dayjs();
    setGasData([]);
    setDateFrom(date);
    if (dateTo < date) {
      setDateTo(date)
    } else if ((aggregation === 'none' || aggregation === 'hour') && dateTo.diff(date, 'day') > maxDaysNoAggregation) {
      setDateTo(date.add(maxDaysNoAggregation, 'day'));
    } else if (aggregation === 'day' && dateTo.diff(date, 'day') > maxDaysDayAggregation) {
      setDateTo(date.add(maxDaysDayAggregation, 'day'));
    }
  };
  const handleDateChangeTo = (x: Dayjs | null) => { 
    const date = x ?? dayjs();
    setGasData([]);
    setDateTo(date);
    if (dateFrom > date) {
      setDateFrom(date)
    } else if (aggregation === 'none' && date.diff(dateFrom, 'day') > maxDaysNoAggregation) {
      setDateFrom(date.add(-maxDaysNoAggregation, 'day'));
    } else if (aggregation === 'day' && date.diff(dateFrom, 'day') > maxDaysDayAggregation) {
      setDateFrom(date.add(-maxDaysDayAggregation, 'day'));
    }
  };
  const handleAggregationChange = (newAggregation: string) => {
    setAggregation(newAggregation);
    let currentDate = dayjs(dateTo);
    
    // Ensure currentDate is not later than mostRecentCompleteDay
    if (mostRecentCompleteDay && currentDate.isAfter(mostRecentCompleteDay)) {
      currentDate = mostRecentCompleteDay;
    }
    
    // Update date range based on aggregation type
    if (newAggregation === 'none' || newAggregation === 'hour') {
      // When switching to "None", set dateFrom to match dateTo
      setDateFrom(currentDate);
      setDateTo(currentDate);
    } else if (newAggregation === 'day') {
      // Get the first day (Monday) and last day (Sunday) of the week containing dateFrom      
      setDateFrom(currentDate.add(-6, 'day')); // Monday
      setDateTo(currentDate); // Sunday
    } else if (newAggregation === 'week') {
      // Get the first day (Monday) and last day (Sunday) of the week containing dateFrom
      const startOfWeek = currentDate.startOf('week').add(-3, 'week'); // Monday
      const endOfWeek = currentDate.endOf('week'); // Sunday
      setDateFrom(startOfWeek);
      setDateTo(endOfWeek);
    } else if (newAggregation === 'month') {
      // Get the first and last day of the month containing dateFrom
      const startOfMonth = currentDate.startOf('month').add(-2, 'month');
      const endOfMonth = currentDate.endOf('month');
      setDateFrom(startOfMonth);
      setDateTo(endOfMonth);
    } else if (newAggregation === 'year') {
      // Get the first and last day of the year containing dateFrom
      const startOfYear = currentDate.startOf('year').add(-1, 'year');
      const endOfYear = currentDate.endOf('year');
      setDateFrom(startOfYear);
      setDateTo(endOfYear);
    }
  };

  const handleLatestClick = () => {
    if (mostRecentCompleteDay) {
      // Apply aggregation logic to set appropriate date range
      if (aggregation === 'none' || aggregation === 'hour') {
        // Keep as single day
        setDateFrom(mostRecentCompleteDay);
        setDateTo(mostRecentCompleteDay);
      } else if (aggregation === 'day') {
        // Set the view to the past week
        setDateFrom(mostRecentCompleteDay.add(-6, 'day'));
        setDateTo(mostRecentCompleteDay);
      } else if (aggregation === 'week') {
        // Get the first day (Monday) and last day (Sunday) of the week
        const startOfWeek = mostRecentCompleteDay.startOf('week').add(-3, 'week');
        const endOfWeek = mostRecentCompleteDay.endOf('week');
        setDateFrom(startOfWeek);
        setDateTo(endOfWeek);
      } else if (aggregation === 'month') {
        // Get the first and last day of the month
        const startOfMonth = mostRecentCompleteDay.startOf('month').add(-2, 'month');
        const endOfMonth = mostRecentCompleteDay.endOf('month');
        setDateFrom(startOfMonth);
        setDateTo(endOfMonth);
      } else if (aggregation === 'year') {
        // Get the first and last day of the year
        const startOfYear = mostRecentCompleteDay.startOf('year').add(-1, 'year');
        const endOfYear = mostRecentCompleteDay.endOf('year');
        setDateFrom(startOfYear);
        setDateTo(endOfYear);
      }
    }
  };

  const handleDownloadFullChart = async () => {
    try {
      // Dynamic import of html2canvas to avoid SSR issues
      const html2canvas = (await import('html2canvas')).default as any;
      
      // Define chart configurations
      const chartConfigs = [
        {
          selector: '[data-chart-id="emissions-1"]',
          name: 'emissions_overview',
          title: 'Emissions Overview',
          condition: () => true // Always include main chart
        },
        {
          selector: '[data-chart-id="histogram-electricity"]',
          name: 'electricity_histogram',
          title: 'Electricity Emissions by Time of Day',
          condition: () => electricityData.length > 0
        },
        {
          selector: '[data-chart-id="histogram-gas"]',
          name: 'gas_histogram',
          title: 'Gas Emissions by Time of Day',
          condition: () => gasData.length > 0
        }
      ];
      
      // Process each chart that meets the condition
      for (const config of chartConfigs) {
        if (!config.condition()) continue;
        
        const chartElement = document.querySelector(config.selector)?.closest('.MuiCard-root') as HTMLElement;
        if (!chartElement) continue;
        
        // Hide elements that should be excluded from download
        const excludeElements = chartElement.querySelectorAll('.exclude-from-download');
        const originalDisplays: string[] = [];
        
        excludeElements.forEach((el) => {
          originalDisplays.push((el as HTMLElement).style.display);
          (el as HTMLElement).style.display = 'none';
        });
        
        // Capture the individual chart
        const canvas = await html2canvas(chartElement, {
          backgroundColor: theme.palette.background.paper,
          scale: 2, // Higher resolution
          useCORS: true,
          allowTaint: true
        });
        
        // Restore original display values
        excludeElements.forEach((el, index) => {
          (el as HTMLElement).style.display = originalDisplays[index];
        });
        
        // Create download link for this chart
        const link = document.createElement('a');
        link.download = `${config.name}_${dateFrom.format('YYYY-MM-DD')}_${dateTo.format('YYYY-MM-DD')}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        
        // Small delay between downloads to prevent browser issues
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.error('Error downloading emissions charts:', error);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadPNG = () => {
    handleDownloadFullChart();
    handleMenuClose();
  };

  const handleDownloadCSV = () => {
    const csvSections: string[] = [];
    
    // Add detailed emissions data if available
    if (electricityData && electricityData.length > 0) {
      csvSections.push('=== Detailed Emissions Data ===');
      const headers = [];
      if (aggregation === 'none' || aggregation === 'hour') { headers.push('Date', 'Time'); }
      else if (aggregation === 'day') { headers.push('Date'); }
      else if (aggregation === 'week') { headers.push('Week commencing'); }
      else if (aggregation === 'month') { headers.push('Month'); }
      else if (aggregation === 'year') { headers.push('Year'); }
      else if (aggregation === 'year') { headers.push('Year'); }
      headers.push('Consumption (kWh)', 'CO₂ intensity (g/kWh)', 'CO₂ emissions (g)');
      
      const csvContent = [
        headers.join(','),
        ...electricityData.map(item => {
          const dateTime = new Date(item.reading_time);
          const date = dateTime.toLocaleDateString('en-GB'); // YYYY-MM-DD format in local time
          const time = dateTime.toLocaleTimeString('en-GB', { 
            hour12: false, 
            hour: '2-digit', 
            minute: '2-digit' 
          }); // HH:MM format in local time
          
          if (aggregation === 'none' || aggregation === 'hour') {
            return [
              date,
              time,
              item.consumption || 0,
              item.gCO_2_per_kWh || 0,
              item.gCO_2 || 0
            ].join(',');
          } else {
            return [
              date,
              item.consumption || 0,
              item.gCO_2_per_kWh || 0,
              item.gCO_2 || 0
            ].join(',');
          }
        })
      ].join('\n');
      
      csvSections.push(csvContent);
    }
    
    // Add electricity percentile summary if available
    if (electricityData.length > 0) {
      csvSections.push('\n=== Electricity Emissions Percentile Summary ===');
      const electricitySummaryData = processPercentileData(percentileData, 'Electricity', theme, undefined);
      
      if (electricitySummaryData.length > 0) {
        const headers = [
          'Emissions Category',
          'Duration',
          'Consumption (kWh)',
          'Emissions (g)',
          'Avg. Emission rate (g/kWh)',
          'Consumption Type'
        ];
        
        const csvContent = [
          headers.join(','),
          ...electricitySummaryData.map(item => [
            item.percentile,
            item.duration,
            item.total_consumption.toFixed(2),
            item.total_emissions.toFixed(2),
            item.emissions_per_kwh.toFixed(2),
            item.consumption_type
          ].join(','))
        ].join('\n');
        
        csvSections.push(csvContent);
      }
    }
    
    // Add gas percentile summary if available
    if (gasData.length > 0) {
      csvSections.push('\n=== Gas Emissions Percentile Summary ===');
      const gasSummaryData = processPercentileData(gasPercentileData, 'Gas', theme, undefined);
      
      if (gasSummaryData.length > 0) {
        const headers = [
          'Emissions Category',
          'Duration',
          'Consumption (kWh)',
          'Emissions (g)',
          'Avg. Emission rate (g/kWh)',
          'Consumption Type'
        ];
        
        const csvContent = [
          headers.join(','),
          ...gasSummaryData.map(item => [
            item.percentile,
            item.duration,
            item.total_consumption.toFixed(2),
            item.total_emissions.toFixed(2),
            item.emissions_per_kwh.toFixed(2),
            item.consumption_type
          ].join(','))
        ].join('\n');
        
        csvSections.push(csvContent);
      }
    }
    
    // Create and download the combined CSV file
    if (csvSections.length > 0) {
      const finalCsvContent = csvSections.join('\n');
      const blob = new Blob([finalCsvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `emissions_data_${dateFrom.format('YYYY-MM-DD')}_${dateTo.format('YYYY-MM-DD')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
    
    handleMenuClose();
  };
 
  // Fetch the most recent complete day when component first loads
  React.useEffect(() => {
    let isCancelled = false;
    
    const fetchMostRecentCompleteDay = async () => {
      try {
        // Check cache first (refreshes daily after 6am)
        const cacheKey = getDailyCacheKey('most-recent-complete-day-type-1', 6);
        const cachedData = getCachedData<{ most_recent_complete_day: string } | { __empty: true }>(cacheKey);
        
        // Check if we have cached preferences
        const prefs = getDashboardPreferences('emissions');
        
        if (cachedData !== null) {
          // Use cached data (even if empty)
          if (!isCancelled) {
            if ('__empty' in cachedData) {
              // Cached empty result
              setMostRecentCompleteDay(null);
            } else if (cachedData.most_recent_complete_day) {
              const mostRecentDate = dayjs(cachedData.most_recent_complete_day);
              setMostRecentCompleteDay(mostRecentDate);
              // Only set default dates if no cached preferences exist
              if (!prefs) {
                setDateFrom(mostRecentDate.add(-6, 'day'));
                setDateTo(mostRecentDate);
              }
            }
            
            setIsInitialized(true);
            setRefreshData(true);
          }
        } else {
          // Cache miss - fetch from API
          const data = await ApiClient.get('api/userconsumption/mostRecentCompleteDay?type=1');
          
          if (!isCancelled) {
            // Always cache the result, even if null
            if (data.most_recent_complete_day) {
              // Store in cache with automatic cleanup
              setDailyCachedData(
                cacheKey, 
                { most_recent_complete_day: data.most_recent_complete_day },
                'most-recent-complete-day-type-1',
                6
              );
              
              const mostRecentDate = dayjs(data.most_recent_complete_day);
              setMostRecentCompleteDay(mostRecentDate);
              // Only set default dates if no cached preferences exist
              if (!prefs) {
                setDateFrom(mostRecentDate.add(-6, 'day'));
                setDateTo(mostRecentDate);
              }
            } else {
              // Cache empty result to prevent repeated API calls
              setDailyCachedData(
                cacheKey,
                { __empty: true },
                'most-recent-complete-day-type-1',
                6
              );
              
              setMostRecentCompleteDay(null);
            }
            
            setIsInitialized(true);
            setRefreshData(true);
          }
        }
      } catch (error) {
        console.error("Error fetching most recent complete day:", error);
        if (!isCancelled) {
          setIsInitialized(true);
        }
      }
    };

    fetchMostRecentCompleteDay();
    
    return () => {
      isCancelled = true;
    };
  }, [isInitialized, refreshTrigger]);

  // Fetch electricity consumption data when dates change
  React.useEffect(() => {
    if (!refreshData || !dateFrom || !dateTo) {
      return;
    }

    const fetchData = async () => {      
        try {
          const params = new URLSearchParams({
            date_from: dateFrom.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            date_to: dateTo.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            type: '1',
            agg: aggregation
          });

          // Check cache first
          const { keyBase, prefix } = createCacheKeyFromParams('userconsumption-get', params);
          const cacheKey = getDailyCacheKey(keyBase, 6);
          const cachedData = getCachedData<DataItem[]>(cacheKey);

          if (cachedData) {
            // Use cached data
            setElectricityData(cachedData);
            setElectricityLoading(false);
          } else {
            // Cache miss - fetch from API
            const data: DataItem[] = await ApiClient.get(`api/userconsumption/get?${params}`);
            
            // Store in cache
            setDailyCachedData(cacheKey, data, prefix, 6);
            
            setElectricityData(data);
            setElectricityLoading(false);
          }
        } catch (error) {
            console.error("Error fetching data:", error);
            setElectricityLoading(false);
        }
    };

    setElectricityLoading(true);
    fetchData();
  }, [dateFrom, dateTo, aggregation, refreshData]);

  // Fetch gas consumption data when dates change
  React.useEffect(() => {
    if (!refreshData || !dateFrom || !dateTo) {
      return;
    }

    const fetchData = async () => {      
        try {
          const params = new URLSearchParams({
            date_from: dateFrom.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
            date_to: dateTo.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
            type: '2',
            agg: aggregation
          });

          // Check cache first
          const { keyBase, prefix } = createCacheKeyFromParams('userconsumption-get', params);
          const cacheKey = getDailyCacheKey(keyBase, 6);
          const cachedData = getCachedData<DataItem[]>(cacheKey);

          if (cachedData) {
            // Use cached data
            setGasData(cachedData);
            setGasLoading(false);
          } else {
            // Cache miss - fetch from API
            const data: DataItem[] = await ApiClient.get(`api/userconsumption/get?${params}`);
            
            // Store in cache
            setDailyCachedData(cacheKey, data, prefix, 6);
            
            setGasData(data);
            setGasLoading(false);
          }
        } catch (error) {
            console.error("Error fetching gas data:", error);
            setGasLoading(false);
        }
    };

    setGasLoading(true);
    fetchData();
  }, [dateFrom, dateTo, aggregation, refreshData]);

  // Fetch percentile data when dates or days filter change
  React.useEffect(() => {
    if (!isInitialized || !dateFrom || !dateTo) {
      return;
    }

    const fetchPercentileData = async () => {
      try {
        const params = new URLSearchParams({
          date_from: dateFrom.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
          date_to: dateTo.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
          type: '1',
          hist_type: 'co2',
          days: electricityDays.toLowerCase()
        });
        
        // Check cache first
        const { keyBase, prefix } = createCacheKeyFromParams('userconsumption-histogram-percentiles', params);
        const cacheKey = getDailyCacheKey(keyBase, 6);
        const cachedData = getCachedData<PercentileData | { __empty: true }>(cacheKey);

        if (cachedData !== null) {
          // Use cached data (even if empty)
          if ('__empty' in cachedData) {
            setPercentileData(null);
          } else {
            setPercentileData(cachedData);
          }
          setPercentileLoading(false);
        } else if (!isFreeUser()) {
          // Cache miss - fetch from API (premium only)
          const data: PercentileData = await ApiClient.get(`api/userconsumption/histogram_percentiles?${params}`);
       
          // Always cache the result, even if empty
          if (data && Object.keys(data).length > 0) {
            setDailyCachedData(cacheKey, data, prefix, 6);
            setPercentileData(data);
          } else {
            // Cache empty result to prevent repeated API calls
            setDailyCachedData(cacheKey, { __empty: true }, prefix, 6);
            setPercentileData(null);
          }
          setPercentileLoading(false);
        } else {
          setPercentileData(null);
          setPercentileLoading(false);
        }
      } catch (error) {
        console.error('Error fetching percentile data:', error);
        setPercentileData(null);
        setPercentileLoading(false);
      }
    };

    if (!isFreeUser()) {
      setPercentileLoading(true);
      fetchPercentileData();
    } else {
      setPercentileData(null);
      setPercentileLoading(false);
    }
  }, [dateFrom, dateTo, isInitialized, electricityDays]);

  // Fetch gas percentile data when dates or days filter change
  React.useEffect(() => {
    if (!isInitialized || !dateFrom || !dateTo) {
      return;
    }

    const fetchGasPercentileData = async () => {
      try {
        const params = new URLSearchParams({
          date_from: dateFrom.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
          date_to: dateTo.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
          type: '2',
          hist_type: 'co2',
          days: gasDays.toLowerCase()
        });
        
        // Check cache first
        const { keyBase, prefix } = createCacheKeyFromParams('userconsumption-histogram-percentiles', params);
        const cacheKey = getDailyCacheKey(keyBase, 6);
        const cachedData = getCachedData<PercentileData | { __empty: true }>(cacheKey);

        if (cachedData !== null) {
          // Use cached data (even if empty)
          if ('__empty' in cachedData) {
            setGasPercentileData(null);
          } else {
            setGasPercentileData(cachedData);
          }
          setGasPercentileLoading(false);
        } else if (!isFreeUser()) {
          // Cache miss - fetch from API (premium only)
          const data: PercentileData = await ApiClient.get(`api/userconsumption/histogram_percentiles?${params}`);
       
          // Always cache the result, even if empty
          if (data && Object.keys(data).length > 0) {
            setDailyCachedData(cacheKey, data, prefix, 6);
            setGasPercentileData(data);
          } else {
            // Cache empty result to prevent repeated API calls
            setDailyCachedData(cacheKey, { __empty: true }, prefix, 6);
            setGasPercentileData(null);
          }
          setGasPercentileLoading(false);
        } else {
          setGasPercentileData(null);
          setGasPercentileLoading(false);
        }
      } catch (error) {
        console.error('Error fetching gas percentile data:', error);
        setGasPercentileData(null);
        setGasPercentileLoading(false);
      }
    };

    if (!isFreeUser()) {
      setGasPercentileLoading(true);
      fetchGasPercentileData();
    } else {
      setGasPercentileData(null);
      setGasPercentileLoading(false);
    }
  }, [dateFrom, dateTo, isInitialized, gasDays]);

  return (
    <Box data-dashboard-id="emissions">
      <Box sx={{ px: { xs: 1, md: 0 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 2 }} 
        onClick={isXs ? () => setControlsExpanded(!controlsExpanded) : undefined}>
          <Typography variant="h1">
            Your CO₂ Emissions
          </Typography>
          {isXs && (
            <IconButton              
              sx={{ ml: 2 }}
            >
              {controlsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
      </Box>
        {!isInitialized ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '400px',
            flexDirection: 'column',
            gap: 2
          }}>
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              Loading emissions data...
            </Typography>
          </Box>
        ) : !mostRecentCompleteDay ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '50vh',
            mt: 4
          }}>
            <Card sx={{ width: '100%', py: 2 }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h6">
                  No emissions data available
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {(() => {
                    const access = localStorage.getItem("access");
                    const hasPremiumAccess = access && access !== 'null' && access !== 'free';
                    
                    if (hasPremiumAccess) {
                      return "No readings are available from your smart meters.";
                    } else {
                      const threeMonthsAgo = dayjs().subtract(3, 'month').format('MMMM YYYY');
                      return `Your smart meters haven't submitted any readings since ${threeMonthsAgo}.`;
                    }
                  })()}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        ) : (
          <>
          <Box sx={{ px: { xs: 1, md: 0 } }}>
              <Box sx={{ 
                width: '100%', 
                maxWidth: '100%',
                mb: 3,
                display: 'grid',
                gridTemplateColumns: '1fr auto',
                alignItems: 'center'
              }}>
              {/* Desktop layout */}
              <Stack direction="row" sx={{ gap: 1, display: { xs: 'none', sm: 'flex' } }}>
                <Box sx={{ minWidth: 'fit-content' }}>
                <Typography variant="h6" gutterBottom>
                  {aggregation === 'none' || aggregation === 'hour' ? 'Date' : 'Date from'}
                </Typography>
                <CustomDatePicker 
                  value={dateFrom} 
                  aggregation={aggregation} 
                  onDateChange={handleDateChangeFrom}
                />
                </Box>
                {aggregation !== 'none' && aggregation !== 'hour' && (
                  <Box sx={{ minWidth: 'fit-content' }}>
                  <Typography variant="h6" gutterBottom>
                    Date to
                  </Typography>
                  <CustomDatePicker 
                    value={dateTo} 
                    aggregation={aggregation} 
                    onDateChange={handleDateChangeTo}
                  />
                  </Box>
                )}
                <SelectAggregation aggregation={aggregation} onChange={handleAggregationChange} />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleLatestClick}
                  sx={{ 
                    minWidth: 'auto',
                    height: '40px', // Match the height of the aggregation dropdown
                    p: '6px 12px', // Add horizontal padding for the text
                    mt: '25px',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                  className="exclude-from-download"
                >
                  Latest
                </Button>
              </Stack>
              { electricityData.length > 0 && electricityData.some(item => item.consumption !== null)
                && gasData.length > 0 && gasData.some(item => item.consumption !== null) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleMenuClick}
                  disabled={electricityData.length === 0 && gasData.length === 0}
                  sx={{ 
                    minWidth: 'auto',
                    height: '40px',
                    p: '6px 12px',
                    mt: '25px',
                    fontSize: '14px',
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    gap: 0.5
                  }}
                  className="exclude-from-download">
                  <DownloadIcon sx={{ fontSize: '16px' }} />
                </Button>
              )}

              {/* Mobile layout */}
              <Collapse in={controlsExpanded} sx={{ width: '100%' }}>
                <Box sx={{ display: { xs: 'block', sm: 'none' }, width: '100%' }}>
                {/* Date pickers - side by side */}
                <Stack direction="row" sx={{ gap: 1, mb: 2 }}>
                  <Box sx={{ width: '50%' }}>
                    <CustomDatePicker 
                      value={dateFrom} 
                      aggregation={aggregation} 
                      onDateChange={handleDateChangeFrom}
                      label={aggregation === 'none' || aggregation === 'hour' ? 'Date' : 'Date from'}
                    />
                  </Box>
                  {aggregation !== 'none' && aggregation !== 'hour' && (
                    <Box sx={{ width: '50%' }}>
                      <CustomDatePicker 
                        value={dateTo} 
                        aggregation={aggregation} 
                        onDateChange={handleDateChangeTo}
                        label="Date to"
                      />
                    </Box>
                  )}
                </Stack>
                
                {/* Buttons row */}
                <Stack direction="row" sx={{ gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                  <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                    <SelectAggregation aggregation={aggregation} onChange={handleAggregationChange} />
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleLatestClick}
                      sx={{ 
                        minWidth: 'auto',
                        height: '40px',
                        p: '6px 12px',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: '25px'
                      }}
                      className="exclude-from-download"
                    >
                      Latest
                    </Button>
                  </Stack>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleMenuClick}
                    disabled={electricityData.length === 0 && gasData.length === 0}
                    sx={{ 
                      minWidth: 'auto',
                      height: '40px',
                      p: '6px 12px',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mt: '25px'
                    }}
                    className="exclude-from-download"
                  >
                    <DownloadIcon sx={{ fontSize: '16px' }} />
                  </Button>
                </Stack>
                </Box>
              </Collapse>
            {anchorEl && (
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                <MenuItem onClick={handleDownloadPNG}>
                  <ListItemIcon>
                    <PictureAsPdfIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Download Charts (PNG)</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleDownloadCSV}>
                  <ListItemIcon>
                    <TableChartIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Download emissions data (CSV)</ListItemText>
                </MenuItem>              
              </Menu>
            )}
              </Box>
            </Box>
        {/* Emissions Chart */}
        <Grid
          container
          spacing={{ xs: 0, sm: 0, md: 2 }}
          columns={12}
          sx={{ mb: (theme) => theme.spacing(2), width: '100%' }}
        >
          <Grid size={{ xs: 12, sm: 12, md: 12 }}>
            <EmissionChart 
              dateFrom={dateFrom.startOf('day').format('YYYY-MM-DD HH:mm:ss')} 
              dateTo={dateTo.endOf('day').format('YYYY-MM-DD HH:mm:ss')} 
              consumptionType={1} 
              aggregation={aggregation} 
              electricityData={electricityData} 
              gasData={gasData}
              loading={electricityLoading || gasLoading} 
            />
          </Grid>
        </Grid>
                
        {/* Consumption Data Grid - Different layout based on user access level */}
        {electricityData.length > 0 && electricityData.some(item => item.consumption !== null) && (
          <>
          {isFreeUser() ? (
            // Direct display without expansion for free users
            <EmissionDataGrid electricityData={electricityData} gasData={gasData} loading={electricityLoading || gasLoading} aggregation={aggregation} />
          ) : (
            // Expandable view for premium users
            <ExpandableDataGrid title="CO₂ Emission Details">
              <EmissionDataGrid electricityData={electricityData} gasData={gasData} loading={electricityLoading || gasLoading} aggregation={aggregation} />
            </ExpandableDataGrid>
          )}

          {/* Hide Histogram sections for free users */}
          {!isFreeUser() && (
            <>
              {/* Electricity Emissions by Time of Day */}
              {electricityData.length > 0 && (
                <>            
                  {/* Electricity Emissions Histogram */}
                  <Grid
                    container
                    spacing={{ xs: 0, sm: 0, md: 2 }}
                    columns={12}
                    sx={{ mb: (theme) => theme.spacing(2), width: '100%' }}
                  >
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                      <Histogram 
                        dateFrom={dateFrom.format('YYYY-MM-DD HH:mm:ss')} 
                        dateTo={dateTo.format('YYYY-MM-DD HH:mm:ss')} 
                        consumptionType={1} 
                        aggregation={aggregation} 
                        loading={electricityLoading || percentileLoading} 
                        percentileData={percentileData}
                        histType='co2'
                        isFreeUser={isFreeUser()}
                        days={electricityDays}
                        onDaysChange={setElectricityDays}
                      />
                    </Grid>
                  </Grid>

                              {/* Electricity Emissions Percentile Summary Grid */}
                  <Grid
                    container
                    spacing={{ xs: 0, sm: 0, md: 2 }}
                    columns={12}
                    sx={{ mb: (theme) => theme.spacing(2), width: '100%' }}
                  >
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                      <HistogramSummaryGrid 
                        percentileData={percentileData}
                        loading={electricityLoading || percentileLoading}
                        consumptionType={1}
                      />
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Gas Emissions by Time of Day */}
              {gasData.length > 0 && (
                <>
                  {/* Gas Emissions Histogram */}
                  <Grid
                    container
                    spacing={{ xs: 0, sm: 0, md: 2 }}
                    columns={12}
                    sx={{ mb: (theme) => theme.spacing(2), width: '100%' }}
                  >
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                      <Histogram 
                        dateFrom={dateFrom.format('YYYY-MM-DD HH:mm:ss')} 
                        dateTo={dateTo.format('YYYY-MM-DD HH:mm:ss')} 
                        consumptionType={2} 
                        aggregation={aggregation} 
                        loading={gasLoading || gasPercentileLoading} 
                        percentileData={gasPercentileData}
                        histType='co2'
                        isFreeUser={isFreeUser()}
                        days={gasDays}
                        onDaysChange={setGasDays}
                      />
                    </Grid>
                  </Grid>

                  {/* Gas Emissions Percentile Summary Grid */}
                  <Grid
                    container
                    spacing={{ xs: 0, sm: 0, md: 2 }}
                    columns={12}
                    sx={{ mb: (theme) => theme.spacing(2), width: '100%' }}
                  >
                    <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                      <HistogramSummaryGrid 
                        percentileData={gasPercentileData}
                        loading={gasLoading || gasPercentileLoading}
                        consumptionType={2}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
            </>
          )}
            </>
          )}
          </>
        )}
      </Box>
  );
}
